import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  addDoc,
  getDoc,
  where,
} from "firebase/firestore";
import MDEditor from "@uiw/react-md-editor";

/* ======================= helpers ======================= */
function getYouTubeVideoId(url) {
  if (!url) return "";
  const reg = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = (typeof url === "string" ? url : url?.url || "").match(reg);
  return match ? match[1] : "";
}

// ---- Google Drive helpers (robust preview/thumbnail) ----
function extractDriveId(url = "") {
  if (!url) return null;
  return (
    url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)?.[1] ||
    url.match(/[?&]id=([a-zA-Z0-9_-]+)/)?.[1] ||
    url.match(/\/uc\?.*?[?&]id=([a-zA-Z0-9_-]+)/)?.[1] ||
    null
  );
}
function drivePreviewUrl(url = "") {
  const id = extractDriveId(url);
  return id ? `https://drive.google.com/uc?export=view&id=${id}` : "";
}
function driveThumbUrl(url = "", size = 1000) {
  const id = extractDriveId(url);
  return id ? `https://drive.google.com/thumbnail?id=${id}&sz=w${size}` : "";
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const upliftOf = (base, pct, isReferred) =>
  isReferred ? round2((Number(base || 0) * Number(pct || 0)) / 100) : 0;

// choose the best thumbnail for the row (now supports services)
function getMainThumb(item) {
  // 1) explicit thumbnail
  if (item.thumbnailUrl) {
    const p = drivePreviewUrl(item.thumbnailUrl);
    if (p) return p;
    return item.thumbnailUrl;
  }
  // 2) products: main or first product image
  const pImgs = Array.isArray(item.productImages) ? item.productImages : [];
  const pMain = pImgs.find((i) => i?.isMain)?.url || pImgs[0]?.url;
  if (pMain) return drivePreviewUrl(pMain) || pMain;
  // 3) services: first service image
  const sImgs = Array.isArray(item.serviceImages) ? item.serviceImages : [];
  const sFirst = sImgs.find((i) => i?.isMain)?.url || sImgs[0]?.url;
  if (sFirst) return drivePreviewUrl(sFirst) || sFirst;
  // 4) courses: first YT thumbnail
  const firstYt =
    Array.isArray(item.youtubeLinks) && item.youtubeLinks.length
      ? item.youtubeLinks[0]
      : null;
  const vid = getYouTubeVideoId(firstYt?.url || firstYt);
  if (vid) return `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;

  // fallback
  return "";
}

/* ===== Gather ALL image URLs for modal (now includes services) ===== */
// Small fixed-size thumb cell (now size-configurable)
function Thumb({ url, className = "w-[96px] h-[64px]" }) {
  const [src, setSrc] = useState(url ? drivePreviewUrl(url) || url : "");
  if (!url)
    return (
      <div
        className={`${className} bg-gray-100 rounded-md border grid place-items-center text-xs text-gray-400`}
      >
        no image
      </div>
    );

  return (
    <img
      src={src}
      alt="thumb"
      className={`${className} object-cover rounded-md border`}
      referrerPolicy="no-referrer"
      onError={() => {
        const t = driveThumbUrl(url, 1000);
        if (t && src !== t) setSrc(t);
      }}
      loading="lazy"
    />
  );
}

/* ======================= Owner Drawer & Referral ======================= */
function SafeVal(v, fb = "-") {
  return v == null || v === "" ? fb : v;
}
const fmtDate = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "-";
  }
};

async function fetchUserProfile(uid) {
  if (!uid) return null;
  try {
    const s = await getDoc(doc(db, "users", uid));
    return s.exists() ? { id: uid, ...s.data() } : null;
  } catch {
    return null;
  }
}

/** Try to resolve referrer */
async function fetchReferralProfile(owner) {
  if (!owner) return null;
  const uid = owner?.referredByUid;
  const code = owner?.referredByReferralCode;

  try {
    if (uid) {
      const s = await getDoc(doc(db, "referral_users", uid));
      if (s.exists()) return { id: s.id, ...s.data() };
    }
    if (code) {
      const q1 = query(
        collection(db, "referral_users"),
        where("referralCode", "==", code),
      );
      const snap = await getDocs(q1);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    blue: "bg-blue-100 text-blue-700",
    gray: "bg-gray-100 text-gray-700",
    red: "bg-red-100 text-red-700",
    purple: "bg-purple-100 text-purple-700",
    amber: "bg-amber-100 text-amber-700",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
}

/* -------- Referral Details Modal -------- */
function Field({ label, value }) {
  return (
    <div className="p-3 border rounded-xl">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium break-all">{SafeVal(value)}</div>
    </div>
  );
}

function ReferralDetailsModal({ open, onClose, referrer }) {
  if (!open) return null;
  const name =
    referrer?.fullName || referrer?.displayName || referrer?.name || "Unknown";

  const avatar =
    referrer?.imageUrl ||
    referrer?.avatar ||
    `https://api.dicebear.com/7.x/initials/svg?fontFamily=Inter&seed=${encodeURIComponent(
      name,
    )}`;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Gradient header */}
        <div className="p-5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white">
          <div className="flex items-center gap-3">
            <img
              src={avatar}
              alt="referrer avatar"
              className="w-12 h-12 rounded-full border object-cover"
              referrerPolicy="no-referrer"
            />
            <div>
              <div className="text-xl font-semibold">Referral — {name}</div>
              <div className="text-sm opacity-90">
                Code: {SafeVal(referrer?.referralCode)}
              </div>
            </div>
            <button
              className="ml-auto text-white/90 text-2xl leading-none"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={referrer?.email} />
            <Field
              label="Phone"
              value={referrer?.phone || referrer?.phoneNumber}
            />
            <Field label="WhatsApp" value={referrer?.whatsapp} />
            <Field label="Telegram" value={referrer?.telegram} />
            <Field label="Address" value={referrer?.address} />
            <Field label="NIC" value={referrer?.nic} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Bank Name" value={referrer?.bankName} />
            <Field label="Account Holder" value={referrer?.accountHolder} />
            <Field label="Bank Branch" value={referrer?.bankBranch} />
            <Field label="Bank Account" value={referrer?.bankAccount} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Joined" value={fmtDate(referrer?.createdAt)} />
            <Field label="User ID" value={referrer?.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- Owner Drawer -------- */
function OwnerDrawer({ open, onClose, item }) {
  const [, setLoading] = useState(false);
  const [owner, setOwner] = useState(null);
  // const [subs, setSubs] = useState([]); // owner’s other submissions (same collection)
  const [counts, setCounts] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    declined: 0,
  });
  // const [error, setError] = useState("");

  // referral
  // const [refLoading, setRefLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [refModal, setRefModal] = useState(false);

  useEffect(() => {
    if (!open || !item?.createdBy) return;
    let alive = true;
    (async () => {
      setLoading(true);
      // setError("");
      try {
        // 1) profile
        const prof = await fetchUserProfile(item.createdBy);

        // 2) owner submissions (in current 'courses' collection)
        const q1 = query(
          collection(db, "courses"),
          where("createdBy", "==", item.createdBy),
          orderBy("createdAt", "desc"),
        );
        const s1 = await getDocs(q1);
        const arr = s1.docs.map((d) => ({ id: d.id, ...d.data() }));

        const approved = arr.filter((x) => x.status === "approved").length;
        const pending = arr.filter((x) => x.status === "pending").length;
        const declined = arr.filter((x) => x.status === "declined").length;

        if (!alive) return;
        setOwner(prof);
        // setSubs(arr);
        setCounts({ total: arr.length, approved, pending, declined });

        // 3) fetch referral summary in parallel (if any)
        // setRefLoading(true);
        const ref = await fetchReferralProfile(prof || {});
        if (!alive) return;

        setReferrer(ref);
      } catch (e) {
        if (!alive) return;
        // setError(e?.message || "Failed to load owner.");
      } finally {
        if (!alive) return;
        setLoading(false);
        // setRefLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, item?.createdBy]);

  if (!open) return null;

  const avatar =
    owner?.profilePicUrl ||
    owner?.avatar ||
    "https://api.dicebear.com/7.x/initials/svg?fontFamily=Inter&seed=" +
      encodeURIComponent(
        owner?.fullName || owner?.displayName || owner?.email || "User",
      );

  const name =
    owner?.fullName || owner?.displayName || owner?.name || "Unknown user";

  const email = owner?.email || "-";
  const phone = owner?.phone || owner?.phoneNumber || "-";

  const kycStatus =
    owner?.kycStatus ||
    owner?.kyc?.status ||
    (owner?.kycApproved
      ? "approved"
      : owner?.kycSubmitted
        ? "pending"
        : "not-submitted");

  const referralKind =
    owner?.referralKind === "instructor"
      ? "instructor"
      : owner?.referralKind || "-";

  const referredBy =
    owner?.referredByUid || owner?.referredByReferralCode || "-";

  return (
    <div className="fixed inset-0 z-[60]">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        {/* Gradient header (modern) */}
        <div className="p-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <img
                src={avatar}
                alt="owner avatar"
                className="w-14 h-14 rounded-full border object-cover"
                referrerPolicy="no-referrer"
              />
              <div>
                <h3 className="text-2xl font-bold leading-tight">{name}</h3>
                <div className="text-xs text-white/80">
                  User ID: {item?.createdBy}
                </div>
              </div>
            </div>
            <button className="text-3xl leading-none -mt-2" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge tone="blue">Owner</Badge>
            {kycStatus === "approved" ? (
              <Badge tone="green">KYC Approved</Badge>
            ) : kycStatus === "pending" ? (
              <Badge tone="yellow">KYC Pending</Badge>
            ) : kycStatus === "rejected" ? (
              <Badge tone="red">KYC Rejected</Badge>
            ) : (
              <Badge tone="gray">KYC Not Submitted</Badge>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Core info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 border rounded-xl">
              <div className="text-xs text-gray-500">Email</div>
              <div className="font-medium break-all">{SafeVal(email)}</div>
            </div>
            <div className="p-3 border rounded-xl">
              <div className="text-xs text-gray-500">Phone</div>
              <div className="font-medium">{SafeVal(phone)}</div>
            </div>
            <div className="p-3 border rounded-xl">
              <div className="text-xs text-gray-500">Referral Kind</div>
              <div className="font-medium capitalize">
                {SafeVal(referralKind)}
              </div>
            </div>
            <div className="p-3 border rounded-xl">
              <div className="text-xs text-gray-500">Referred By (ID/Code)</div>
              <div className="font-medium break-all">{SafeVal(referredBy)}</div>
            </div>
          </div>

          {/* Referral summary */}
          <div className="border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b font-semibold flex items-center justify-between">
              <div>Referrer</div>
              <div className="text-xs text-gray-500">{/* status */}</div>
            </div>

            {/* resolved */}
            {/* Inlined for brevity; original component preserved above */}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 border rounded-xl text-center">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-xl font-semibold">{counts.total}</div>
            </div>
            <div className="p-3 border rounded-xl text-center">
              <div className="text-xs text-gray-500">Approved</div>
              <div className="text-xl font-semibold text-green-700">
                {counts.approved}
              </div>
            </div>
            <div className="p-3 border rounded-xl text-center">
              <div className="text-xs text-gray-500">Pending</div>
              <div className="text-xl font-semibold text-amber-700">
                {counts.pending}
              </div>
            </div>
            <div className="p-3 border rounded-xl text-center">
              <div className="text-xs text-gray-500">Declined</div>
              <div className="text-xl font-semibold text-red-700">
                {counts.declined}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Referral details full modal */}
      <ReferralDetailsModal
        open={refModal}
        onClose={() => setRefModal(false)}
        referrer={referrer}
      />
    </div>
  );
}

/* ===== UI helper: clamp/expand long text ===== */

function MDClamp({ md = "", limit = 100 }) {
  const [open, setOpen] = useState(false);
  const mdToPlain = (s = "") =>
    (s || "")
      .replace(/!\[[^\]]*]\([^)]*\)/g, "") // images
      .replace(/\[([^\]])]\([^)]*\)/g, "$1") // links -> text
      .replace(/```[\s\S]*?```/g, "") // code blocks
      .replace(/`([^`])`/g, "$1") // inline code
      .replace(/[*_~>#-]/g, "") // md tokens
      .replace(/\n/g, " ")
      .trim();
  const plain = mdToPlain(md);
  const needsClamp = plain.length > limit;
  const teaser = plain.slice(0, limit);

  return (
    <div className="relative">
      {open || !needsClamp ? (
        <div data-color-mode="light" className="markdown-body">
          <MDEditor.Markdown source={md || ""} />
        </div>
      ) : (
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
          {teaser}
          <span className="text-gray-400">…</span>
        </p>
      )}
      {needsClamp && (
        <button
          className="mt-2 text-blue-700 hover:underline font-medium text-sm"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
}

/* ======================= Main ======================= */
export default function Dashboard() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [actionId, setActionId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [modalAction, setModalAction] = useState(""); // "approve" | "decline" | "edit"
  const [declineReason, setDeclineReason] = useState("");
  const [isFull, setIsFull] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // pagination (client-side)
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // NEW: tabs include services
  const [tab, setTab] = useState("courses"); // 'courses' | 'products' | 'services'
  const [refMap, setRefMap] = useState({});

  // whenever listings load or tab/pageSize change, go to page 1
  useEffect(() => {
    setPage(1);
  }, [listings, tab, pageSize]);

  // Load all items + referral metadata
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(
          query(collection(db, "courses"), orderBy("createdAt", "desc")),
        );
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setListings(arr);

        // fetch instructor referral flags
        const uids = Array.from(
          new Set(arr.map((x) => x.createdBy).filter(Boolean)),
        );
        const map = {};
        await Promise.all(
          uids.map(async (uid) => {
            try {
              const s = await getDoc(doc(db, "users", uid));
              if (s.exists()) {
                const u = s.data() || {};
                const isReferred =
                  u?.referralKind === "instructor" &&
                  (!!u?.referredByUid || !!u?.referredByReferralCode);
                map[uid] = {
                  isReferred,
                  upliftPct: Number(u?.priceUpliftPct ?? 15),
                };
              } else {
                map[uid] = { isReferred: false, upliftPct: 15 };
              }
            } catch {
              map[uid] = { isReferred: false, upliftPct: 15 };
            }
          }),
        );
        setRefMap(map);
      } catch (error) {
        console.error("admin dashboard listings load failed", error);
        setListings([]);
        setRefMap({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Approve/Decline
  const handleStatus = async (id, status, extraFields = {}) => {
    // setActionId(id);
    await updateDoc(doc(db, "courses", id), { status, ...extraFields });
    // setActionId(null);
    setListings((old) =>
      old.map((d) => (d.id === id ? { ...d, status, ...extraFields } : d)),
    );
  };

  // slices
  const courses = listings.filter(
    (x) => (x.type || "").toLowerCase() === "course",
  );
  const products = listings.filter(
    (x) => (x.type || "").toLowerCase() === "product",
  );
  const services = listings.filter(
    (x) => (x.type || "").toLowerCase() === "service",
  );
  const rows =
    tab === "courses" ? courses : tab === "products" ? products : services;

  // derive current page window
  const total = rows.length;
  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  const pageRows = rows.slice(startIdx, endIdx);

  // helpers
  const canPrev = page > 1;
  const canNext = endIdx < total;

  // For services, compute base using hourly if provided
  function getInstructorBase(item) {
    const t = String(item?.type || "").toLowerCase();

    const toNum = (v, fb = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fb;
    };

    if (t === "service") {
      const pricingType = String(
        item?.pricingType || (item?.isHourly ? "hourly" : "fixed"),
      ).toLowerCase();

      if (pricingType === "hourly") {
        const rate = toNum(item?.hourlyRate, 0);
        const minH = Math.max(1, Math.floor(toNum(item?.minHours, 1)));
        return rate * minH;
      }

      // fixed service base
      return toNum(item?.price, 0);
    }

    // course/product base
    return toNum(item?.price, 0);
  }

  return (
    <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Admin Review Panel</h1>
        <p className="text-gray-500 text-sm">
          Review submissions, verify owners, and manage approvals.
        </p>
      </div>

      {/* ---- Tabs ---- */}
      <div className="mb-4 inline-flex rounded-2xl border bg-white p-1 shadow-sm">
        <button
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
            tab === "courses"
              ? "bg-blue-600 text-white shadow"
              : "text-blue-700 hover:bg-blue-50"
          }`}
          onClick={() => setTab("courses")}
        >
          Courses ({courses.length})
        </button>
        <button
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
            tab === "products"
              ? "bg-blue-600 text-white shadow"
              : "text-blue-700 hover:bg-blue-50"
          }`}
          onClick={() => setTab("products")}
        >
          Products ({products.length})
        </button>
        <button
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
            tab === "services"
              ? "bg-blue-600 text-white shadow"
              : "text-blue-700 hover:bg-blue-50"
          }`}
          onClick={() => setTab("services")}
        >
          Services ({services.length})
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : (
        <div>
          {/* Sticky header + scrollable table */}
          <div className="rounded-2xl shadow ring-1 ring-black/5">
            <div className="overflow-auto max-h-[70vh]">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="text-gray-500">
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">#</th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">
                      Created
                    </th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">
                      Thumbnail
                    </th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">Title</th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">Type</th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">
                      Content
                    </th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">
                      End Price
                    </th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">Split</th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">Status</th>
                    <th className="p-3 sticky top-0 z-10 bg-gray-50">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pageRows.map((item, i) => {
                    const instructorBasePrice = getInstructorBase(item);
                    const company = Number(item.company || 0);
                    const commission = Number(item.commission || 0);
                    const service = Number(item.service || 0);
                    const others = Number(item.others || 0);

                    const defaultPct = 15;
                    const instrMeta = refMap[item.createdBy] || {
                      isReferred: false,
                      upliftPct: defaultPct,
                    };
                    const isReferred = Boolean(
                      item.referredInstructor ?? instrMeta.isReferred,
                    );
                    const upliftPct = Number(
                      item.priceUpliftPct ?? instrMeta.upliftPct ?? defaultPct,
                    );
                    const uplift = upliftOf(
                      instructorBasePrice,
                      upliftPct,
                      isReferred,
                    );

                    const computedEnd =
                      instructorBasePrice +
                      uplift +
                      company +
                      commission +
                      service +
                      others;
                    const endPrice =
                      typeof item.finalPrice === "number"
                        ? item.finalPrice
                        : computedEnd;

                    // support service youtube array too
                    const vids = Array.isArray(item.youtubeLinks)
                      ? item.youtubeLinks
                      : Array.isArray(item.serviceYoutubeLinks)
                        ? item.serviceYoutubeLinks
                        : [];
                    const showCount = 2;
                    const extra = Math.max(0, vids.length - showCount);

                    const thumb = getMainThumb(item);

                    return (
                      <tr
                        key={item.id}
                        className="border-b last:border-none hover:bg-blue-50/40 transition"
                      >
                        <td className="p-3">{startIdx + i + 1}</td>
                        <td className="p-3 min-w-[140px] align-center">
                          <span className="text-gray-700">
                            {fmtDate(item.createdAt)}
                          </span>
                        </td>
                        <td className="p-3">
                          <Thumb url={thumb} />
                        </td>
                        <td className="p-3 font-medium">
                          <div className="flex items-center gap-2">
                            <span className="truncate">
                              {item.title?.length > 30
                                ? item.title.slice(0, 30) + "…"
                                : item.title}
                            </span>
                            {isReferred && (
                              <span className="ml-1 align-middle bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap">
                                Referred +{upliftPct}%
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                            {item.type}
                          </span>
                        </td>
                        <td className="p-3 min-w-[120px] align-top">
                          {vids.length === 0 ? (
                            <span className="text-gray-400">-</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {vids.slice(0, showCount).map((y, j) => (
                                <a
                                  key={j}
                                  href={typeof y === "string" ? y : y.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-xs truncate"
                                  title={typeof y === "string" ? y : y.url}
                                >
                                  Video {j + 1}
                                </a>
                              ))}
                              {extra > 0 && (
                                <span className="text-[11px] text-gray-500">
                                  +{extra} more
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-3 font-bold text-lg">
                          $
                          {Number(endPrice).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-3 text-xs text-gray-700">
                          <div>
                            <b className="text-green-700">Instructor:</b> $
                            {instructorBasePrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          {isReferred && (
                            <div>
                              <b className="text-amber-700">
                                Uplift (+{upliftPct}%):
                              </b>{" "}
                              $
                              {uplift.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </div>
                          )}
                          <div>
                            <b className="text-blue-700">Company:</b> $
                            {company.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div>
                            <b className="text-purple-700">Commission:</b> $
                            {commission.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div>
                            <b className="text-pink-700">Service:</b> $
                            {service.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div>
                            <b className="text-orange-700">Others:</b> $
                            {others.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                        </td>
                        <td className="p-3">
                          <span
                            className={
                              item.status === "approved"
                                ? "bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold"
                                : item.status === "pending"
                                  ? "bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold"
                                  : "bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold"
                            }
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="p-3 flex flex-wrap gap-1">
                          {/* View OWNER */}
                          <button
                            className="bg-gray-100 text-gray-800 px-3 py-1 rounded hover:bg-gray-200 text-xs"
                            onClick={() => {
                              setModalItem(null);
                              setIsFull(false);
                              setViewItem(item);
                              setViewOpen(true);
                            }}
                          >
                            View
                          </button>

                          {/* ---- ACTIONS ---- */}
                          {item.status === "pending" ? (
                            <>
                              <button
                                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-xs"
                                onClick={() => {
                                  const instrMeta = refMap[item.createdBy] || {
                                    isReferred: false,
                                    upliftPct: 15,
                                  };
                                  setModalItem({
                                    ...item,
                                    company: item.company || "",
                                    commission: item.commission || "",
                                    service: item.service || "",
                                    others: item.others || "",
                                    __isReferred: Boolean(
                                      item.referredInstructor ??
                                      instrMeta.isReferred,
                                    ),
                                    __upliftPct: Number(
                                      item.priceUpliftPct ??
                                        instrMeta.upliftPct ??
                                        15,
                                    ),
                                  });
                                  setModalAction("approve");
                                  setModalOpen(true);
                                }}
                              >
                                Approve
                              </button>
                              <button
                                className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 text-xs"
                                onClick={() => {
                                  setModalItem({ ...item });
                                  setModalAction("decline");
                                  setDeclineReason("");
                                  setModalOpen(true);
                                }}
                              >
                                Decline
                              </button>
                            </>
                          ) : item.status === "approved" ? (
                            <>
                              <button
                                className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 text-xs"
                                onClick={() => {
                                  setModalItem({ ...item });
                                  setModalAction("decline");
                                  setDeclineReason("");
                                  setModalOpen(true);
                                }}
                              >
                                Decline
                              </button>
                              <button
                                className="bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 text-xs"
                                onClick={() => {
                                  const instrMeta = refMap[item.createdBy] || {
                                    isReferred: false,
                                    upliftPct: 15,
                                  };
                                  setModalItem({
                                    ...item,
                                    company: item.company || "",
                                    commission: item.commission || "",
                                    service: item.service || "",
                                    others: item.others || "",
                                    __isReferred: Boolean(
                                      item.referredInstructor ??
                                      instrMeta.isReferred,
                                    ),
                                    __upliftPct: Number(
                                      item.priceUpliftPct ??
                                        instrMeta.upliftPct ??
                                        15,
                                    ),
                                  });
                                  setModalAction("edit");
                                  setModalOpen(true);
                                }}
                              >
                                Edit
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-xs"
                                onClick={() => {
                                  const instrMeta = refMap[item.createdBy] || {
                                    isReferred: false,
                                    upliftPct: 15,
                                  };
                                  setModalItem({
                                    ...item,
                                    company: item.company || "",
                                    commission: item.commission || "",
                                    service: item.service || "",
                                    others: item.others || "",
                                    __isReferred: Boolean(
                                      item.referredInstructor ??
                                      instrMeta.isReferred,
                                    ),
                                    __upliftPct: Number(
                                      item.priceUpliftPct ??
                                        instrMeta.upliftPct ??
                                        15,
                                    ),
                                  });
                                  setModalAction("approve");
                                  setModalOpen(true);
                                }}
                              >
                                Approve
                              </button>
                              <button
                                className="bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 text-xs"
                                onClick={() => {
                                  const instrMeta = refMap[item.createdBy] || {
                                    isReferred: false,
                                    upliftPct: 15,
                                  };
                                  setModalItem({
                                    ...item,
                                    company: item.company || "",
                                    commission: item.commission || "",
                                    service: item.service || "",
                                    others: item.others || "",
                                    __isReferred: Boolean(
                                      item.referredInstructor ??
                                      instrMeta.isReferred,
                                    ),
                                    __upliftPct: Number(
                                      item.priceUpliftPct ??
                                        instrMeta.upliftPct ??
                                        15,
                                    ),
                                  });
                                  setModalAction("edit");
                                  setModalOpen(true);
                                }}
                              >
                                Edit
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination footer */}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              Rows per page:
              <select
                className="ml-2 border rounded-lg px-2 py-1 bg-white"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 20, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-600">
              {total === 0
                ? "0–0 of 0"
                : `${startIdx + 1}–${endIdx} of ${total}`}
            </div>
            <div className="inline-flex rounded-lg border overflow-hidden">
              <button
                onClick={() => canPrev && setPage((p) => p - 1)}
                disabled={!canPrev}
                className={`px-3 py-2 text-sm ${
                  !canPrev ? "text-gray-400 bg-gray-50" : "hover:bg-gray-50"
                }`}
              >
                ‹ Prev
              </button>
              <button
                onClick={() => canNext && setPage((p) => p + 1)}
                disabled={!canNext}
                className={`px-3 py-2 text-sm border-l ${
                  !canNext ? "text-gray-400 bg-gray-50" : "hover:bg-gray-50"
                }`}
              >
                Next ›
              </button>
            </div>
          </div>
        </div>
      )}

      <OwnerDrawer
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        item={viewItem}
      />

      {/* ------------ Approve/Edit/Decline Modal ------------ */}
      {modalOpen && modalItem && (
        <div
          className={`fixed inset-0 ${
            isFull ? "bg-black/50" : "bg-black/30"
          } z-50`}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0"
            onClick={() => {
              setModalOpen(false);
              setIsFull(false);
            }}
          />
          <div
            className={[
              "absolute left-1/2 top-1/2 bg-white shadow-2xl",
              "flex flex-col overflow-hidden",
              isFull
                ? "w-screen h-screen -translate-x-1/2 -translate-y-1/2 rounded-none"
                : "w-full max-w-3xl max-h-[90vh] -translate-x-1/2 -translate-y-1/2 rounded-2xl",
            ].join(" ")}
          >
            <div className="absolute top-3 right-3 z-20 inline-flex gap-2">
              <button
                className="rounded-lg border bg-white/90 backdrop-blur px-3 py-1.5 text-sm hover:bg-white"
                onClick={() => setIsFull((v) => !v)}
                title={isFull ? "Exit full screen" : "Full screen"}
              >
                {isFull ? "⤢ Exit" : "⤢ Full"}
              </button>
              <button
                className="rounded-lg border bg-white/90 backdrop-blur px-3 py-1.5 text-sm hover:bg-white"
                onClick={() => {
                  setModalOpen(false);
                  setIsFull(false);
                }}
                title="Close"
              >
                × Close
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
                <div
                  className={`${isFull ? "px-6 pt-6 pb-4" : "px-6 pt-5 pb-4"}`}
                >
                  <h2 className="text-xl font-bold">
                    {modalAction === "approve"
                      ? "Approve"
                      : modalAction === "edit"
                        ? "Edit Submission"
                        : "Decline"}{" "}
                    Submission
                  </h2>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl border bg-white">
                      <div className="text-xs text-gray-500">Type</div>
                      <div className="font-medium">{modalItem.type}</div>
                    </div>
                    <div className="p-3 rounded-xl border bg-white">
                      <div className="text-xs text-gray-500">Category</div>
                      <div className="font-medium break-words">
                        {modalItem.category || "-"}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl border bg-white sm:col-span-2">
                      <div className="text-xs text-gray-500">Title</div>
                      <div className="font-medium break-words flex items-center gap-2">
                        <span>{modalItem.title}</span>
                        {modalItem.__isReferred && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
                            Referred +{modalItem.__upliftPct || 15}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl border bg-white sm:col-span-2">
                      <div className="text-xs text-gray-500">Description</div>
                      <MDClamp
                        md={
                          modalItem.description ||
                          modalItem.productMainDesc ||
                          modalItem.serviceDesc
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-6 pt-4">
                {/* Price recap */}
                <div className="mb-3">
                  <b>Price (Instructor):</b>{" "}
                  {(() => {
                    const t = (modalItem.type || "").toLowerCase();
                    if (
                      t === "service" &&
                      (modalItem.pricingType ||
                        (modalItem.isHourly ? "hourly" : "fixed")) === "hourly"
                    ) {
                      const rate = Number(modalItem.hourlyRate || 0);
                      const minH = Number(modalItem.minHours || 1);
                      const base = rate * minH;
                      return `$${rate.toFixed(
                        2,
                      )}/hr × ${minH}h = $${base.toFixed(2)}`;
                    }
                    const base = Number(modalItem.price || 0);
                    return `$${base.toFixed(2)}`;
                  })()}
                </div>

                {/* Fees */}
                {(modalAction === "approve" || modalAction === "edit") && (
                  <>
                    <div className="mb-3 flex gap-2 flex-wrap">
                      {[
                        { key: "company", label: "Company" },
                        { key: "commission", label: "Commission" },
                        { key: "service", label: "Service" },
                        { key: "others", label: "Others" },
                      ].map((f) => (
                        <div key={f.key}>
                          <label className="block font-semibold text-xs mb-1">
                            {f.label}
                          </label>
                          <input
                            type="number"
                            min={0}
                            className="p-2 w-24 border rounded-xl bg-gray-50"
                            value={modalItem[f.key] ?? ""}
                            onChange={(e) =>
                              setModalItem((m) => ({
                                ...m,
                                [f.key]: e.target.value,
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>

                    <div className="mb-3">
                      <b>Final Price: </b>
                      {(() => {
                        // const t = (modalItem.type || "").toLowerCase();
                        const company = Number(modalItem.company || 0);
                        const commission = Number(modalItem.commission || 0);
                        const service = Number(modalItem.service || 0);
                        const others = Number(modalItem.others || 0);
                        const upliftPct = Number(modalItem.__upliftPct || 15);
                        const isRef = Boolean(modalItem.__isReferred);
                        const base = getInstructorBase(modalItem);
                        const uplift = upliftOf(base, upliftPct, isRef);
                        const total =
                          base +
                          uplift +
                          company +
                          commission +
                          service +
                          others;
                        return `$${total.toFixed(2)}`;
                      })()}
                    </div>
                  </>
                )}

                {/* Decline reason */}
                {modalAction === "decline" && (
                  <div className="mb-3">
                    <label className="block font-semibold text-sm mb-1">
                      Reason for decline <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      className="w-full border rounded-xl p-3 min-h-[96px] bg-gray-50"
                      placeholder="Explain clearly why this submission was declined…"
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      maxLength={500}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      This reason will be included in the notification to the
                      owner.
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6 sticky bottom-0 bg-white py-3 border-t">
                  {(modalAction === "approve" || modalAction === "edit") && (
                    <button
                      className="px-4 py-2 rounded-xl text-white bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        const base = getInstructorBase(modalItem);
                        const company = Number(modalItem.company || 0);
                        const commission = Number(modalItem.commission || 0);
                        const service = Number(modalItem.service || 0);
                        const others = Number(modalItem.others || 0);
                        const upliftPct = Number(modalItem.__upliftPct || 15);
                        const isRef = Boolean(modalItem.__isReferred);
                        const uplift = upliftOf(base, upliftPct, isRef);
                        const finalPrice =
                          base +
                          uplift +
                          company +
                          commission +
                          service +
                          others;

                        await handleStatus(modalItem.id, "approved", {
                          company,
                          commission,
                          service,
                          others,
                          finalPrice,
                          basePrice: base,
                          referredInstructor: isRef,
                          priceUpliftPct: upliftPct,
                          priceUpliftAmount: uplift,
                        });

                        const instructorId = modalItem.createdBy;
                        if (instructorId) {
                          await addDoc(
                            collection(
                              db,
                              "users",
                              instructorId,
                              "notifications",
                            ),
                            {
                              message: `Your ${modalItem.type} "${modalItem.title}" was approved.`,
                              status: "approved",
                              createdAt: new Date(),
                              refId: modalItem.id,
                            },
                          );
                        }
                        setModalOpen(false);
                        setIsFull(false);
                      }}
                    >
                      Approve
                    </button>
                  )}

                  {modalAction === "decline" && (
                    <button
                      className={`px-4 py-2 rounded-xl text-white ${
                        declineReason.trim()
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-red-300 cursor-not-allowed"
                      }`}
                      disabled={!declineReason.trim()}
                      onClick={async () => {
                        const reason = (declineReason || "").trim();
                        if (!reason) return;

                        await handleStatus(modalItem.id, "declined", {
                          declineReason: reason,
                          declinedAt: new Date(),
                        });

                        const instructorId = modalItem.createdBy;
                        if (instructorId) {
                          await addDoc(
                            collection(
                              db,
                              "users",
                              instructorId,
                              "notifications",
                            ),
                            {
                              message: `Your ${modalItem.type} "${modalItem.title}" was declined.`,
                              reason,
                              status: "declined",
                              createdAt: new Date(),
                              refId: modalItem.id,
                            },
                          );
                        }
                        setModalOpen(false);
                        setIsFull(false);
                      }}
                    >
                      Decline
                    </button>
                  )}

                  <button
                    className="px-4 py-2 rounded-xl border"
                    onClick={() => {
                      setModalOpen(false);
                      setIsFull(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
